import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body1,
    Button,
    ConditionalComponent,
    Heading1,
    OutlinedCard,
    Stack,
    Subtitle1,
} from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../graphql';
import type { ControlledSubstanceCode } from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';
import type { OfferFragment } from '../../types';
import { getAbsoluteRoute } from '../../util';
import { hasPHX502 } from '../../util/offers/sort';
import { shouldShowMemberCard } from '../../util/patient/should-show-member-card';
import { PharmacyDoesNotCarryMedicationAlert } from '../common/PharmacyDoesNotCarryMedicationAlert';
import { Pricing } from '../common/pricing/Pricing';
import { PharmacyProviderDetails } from '../preview-details/components/PharmacyProviderDetails';
import { ShowPlanCard } from '../shopping/ShowPlanCard';

export type PrescriptionOverViewProps = {
    providerOffersId: string;
    offer: OfferFragment;
    header?: React.ReactNode;
    controlledSubstanceCode?: ControlledSubstanceCode | null;
};

export const PrescriptionOverview = ({
    offer,
    header,
    providerOffersId,
    controlledSubstanceCode,
}: PrescriptionOverViewProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const patient = getFragment(offer.patient);
    const { rxTransferEnabled } = useFlags();
    const pharmacyDoesNotCarryMedication = hasPHX502(offer);
    const fillLocation = getFragment(offer.pharmacyProvider);
    const showMemberCard = shouldShowMemberCard(
        fillLocation?.name,
        offer.__typename === 'InsuranceOffer'
    );

    return (
        <Stack gap="md">
            {header}
            <Heading1>{t('transfer.overview')}</Heading1>
            <ConditionalComponent condition={pharmacyDoesNotCarryMedication}>
                <PharmacyDoesNotCarryMedicationAlert />
            </ConditionalComponent>
            <ConditionalComponent condition={!pharmacyDoesNotCarryMedication}>
                <Pricing offer={offer} />
            </ConditionalComponent>
            <PharmacyProviderDetails offer={offer} />
            <ConditionalComponent condition={!pharmacyDoesNotCarryMedication}>
                {patient && showMemberCard && (
                    <ShowPlanCard
                        plan={offer.plan}
                        patient={patient}
                        description={t('transfer.planCardDescription')}
                        heading={t('transfer.planCardHeading')}
                    />
                )}

                {rxTransferEnabled && (
                    <OutlinedCard
                        header={
                            <Subtitle1>
                                {t('transfer.atAnotherPharmacy')}
                            </Subtitle1>
                        }
                    >
                        {controlledSubstanceCode ? (
                            <Body1>{t('transfer.controlledSubstances')}</Body1>
                        ) : (
                            <Stack gap="sm">
                                <Body1>{t('transfer.weCanHelp')}</Body1>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        navigate(
                                            getAbsoluteRoute(
                                                'transfer.location.root',
                                                {
                                                    offerId: offer.id,
                                                    providerOffersId,
                                                }
                                            )
                                        );
                                    }}
                                >
                                    {t('transfer.transferButton')}
                                </Button>
                            </Stack>
                        )}
                    </OutlinedCard>
                )}

                <OutlinedCard
                    header={<Subtitle1>{t('transfer.newScript')}</Subtitle1>}
                >
                    <Stack gap="sm">
                        <Body1>{t('transfer.askYourDoctor')}</Body1>
                    </Stack>
                </OutlinedCard>
            </ConditionalComponent>
        </Stack>
    );
};
